import { DateRange } from '../types/date.type';

const RAW_MIXTE_REFERENCE_TIMESTAMP = 1515301200000; // 2018-01-07T05:00:00.000Z
export const MIXTE_PERIOD_DURATION = 14;

const RAW_LUMP_SUM_REFERENCE_TIMESTAMP = 1515301200000; // 2018-01-07T05:00:00.000Z
export const LUMP_SUM_PERIOD_DURATION = 7;

type InstantiateDateFunction = (timestamp: number) => Date;

const differenceInDays = (date1: Date, date2: Date): number => {
  const diffTime = Math.abs(date2.valueOf() - date1.valueOf());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const addDays = (date: Date, days: number, instantiateDate: InstantiateDateFunction): Date => {
  const result = instantiateDate(date.valueOf());
  result.setDate(result.getDate() + days);
  return result;
};

const subtractDays = (date: Date, days: number, instantiateDate: InstantiateDateFunction): Date => {
  const result = instantiateDate(date.valueOf());
  result.setDate(result.getDate() - days);
  return result;
};

const subtractMilliseconds = (date: Date, milliseconds: number, instantiateDate: InstantiateDateFunction): Date => {
  return instantiateDate(date.valueOf() - milliseconds);
};

/**
 * The periods are based on RAMQ so the date must be at 00:00 EST/EDT or else the period will be off
 * @param date The date to infer the period from. Must be at 00:00 EST/EDT
 * @param referenceDate The reference date for which we know the start date of the period
 * @param periodDuration The duration of the period in days
 * @param instantiateDate The function to instantiate a date from a timestamp (to instantiate a TZDate for example)
 * @returns
 */
const inferDateRangeFromDate = (
  date: Date,
  referenceDate: Date,
  periodDuration: number,
  instantiateDate: InstantiateDateFunction,
): DateRange => {
  const numberOfDaysBetweenDateAndDefaultBillingStartPeriodDate = differenceInDays(date, referenceDate);
  const daysFromNearestStartDate = numberOfDaysBetweenDateAndDefaultBillingStartPeriodDate % periodDuration;
  const start = subtractDays(date, daysFromNearestStartDate, instantiateDate);
  const endDay = addDays(start, periodDuration, instantiateDate);
  const end = subtractMilliseconds(endDay, 1, instantiateDate);

  return { start, end };
};

/**
 * Infer the date range for a lump sum period from a given date
 * @param date The date to infer the period from. Must be at 00:00 EST/EDT
 * @param instantiateDate The function to instantiate a date from a timestamp (to instantiate a TZDate for example)
 * @returns The date range for the lump sum period
 */
export const inferLumpSumDateRangeFromDate = (
  date: Date,
  instantiateDate: InstantiateDateFunction = (date) => new Date(date),
): DateRange => {
  return inferDateRangeFromDate(
    date,
    instantiateDate(RAW_LUMP_SUM_REFERENCE_TIMESTAMP),
    LUMP_SUM_PERIOD_DURATION,
    instantiateDate,
  );
};

/**
 * Infer the date range for a mixte period from a given date
 * @param date The date to infer the period from. Must be at 00:00 EST/EDT
 * @param instantiateDate The function to instantiate a date from a timestamp (to instantiate a TZDate for example)
 * @returns The date range for the mixte period
 */
export const inferMixteDateRangeFromDate = (
  date: Date,
  instantiateDate: InstantiateDateFunction = (date) => new Date(date),
): DateRange => {
  return inferDateRangeFromDate(
    date,
    instantiateDate(RAW_MIXTE_REFERENCE_TIMESTAMP),
    MIXTE_PERIOD_DURATION,
    instantiateDate,
  );
};

/**
 * Calculate all periods for a given month based on periodStart and periodDuration
 * @param monthStart The start of the month to calculate periods for
 * @param periodStart The reference start date for periods
 * @param periodDuration The duration of each period in days
 * @param instantiateDate The function to instantiate a date from a timestamp (to instantiate a TZDate for example)
 * @returns Array of date ranges for all periods that overlap with the given month
 */
export const calculatePeriodsForMonth = (
  monthStart: Date,
  periodStart: Date,
  periodDuration: number,
  instantiateDate: InstantiateDateFunction = (date) => new Date(date),
): DateRange[] => {
  const periods: DateRange[] = [];

  const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);

  // Find the first period that starts before or during this month
  const daysFromPeriodStart = differenceInDays(monthStart, periodStart);
  const periodsToSubtract = Math.ceil(Math.abs(daysFromPeriodStart) / periodDuration);

  let currentPeriodStart: Date;
  if (daysFromPeriodStart >= 0) {
    currentPeriodStart = addDays(periodStart, periodsToSubtract * periodDuration, instantiateDate);
  } else {
    currentPeriodStart = subtractDays(periodStart, periodsToSubtract * periodDuration, instantiateDate);
  }

  while (currentPeriodStart > monthStart) {
    currentPeriodStart = subtractDays(currentPeriodStart, periodDuration, instantiateDate);
  }
  while (addDays(currentPeriodStart, periodDuration, instantiateDate) <= monthStart) {
    currentPeriodStart = addDays(currentPeriodStart, periodDuration, instantiateDate);
  }

  // Generate all periods that overlap with the month
  let periodStartDate = currentPeriodStart;
  while (periodStartDate <= monthEnd) {
    const periodEndDate = subtractMilliseconds(
      addDays(periodStartDate, periodDuration, instantiateDate),
      1,
      instantiateDate,
    );

    // Only include periods that overlap with the month
    if (periodEndDate >= monthStart && periodStartDate <= monthEnd) {
      periods.push({
        start: periodStartDate,
        end: periodEndDate,
      });
    }

    periodStartDate = addDays(periodStartDate, periodDuration, instantiateDate);
  }

  return periods;
};
